import React, { useCallback } from 'react';
import { FormField, Input, InputTypes } from 'taboola-ultimate-ui';
import TooltipSection from 'modules/campaigns/modules/common-campaign-form/components/TooltipSection';
import { withIndication } from 'modules/errors';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { useFormValidatedValue, validationFunctions } from 'modules/taboola-common-frontend-modules/validations';
import { LANDING_PAGE_URLS } from '../../../consts/urls';
import styles from './title.module.scss';

const InputWithIndication = withIndication(Input);

const UrlPrefixIcon = () => <div className={styles['url-prefix']}>{LANDING_PAGE_URLS.PRODUCTION}</div>;

// Only allow lowercase letters, numbers, and dashes
const SLUG_SANITIZATION_REGEX = /[^a-z0-9-]/g;

const validations = [
    {
        validationFn: (value, options, { skipValidations }) => {
            if (skipValidations) {
                return true;
            }

            return validationFunctions.isNotEmptyString(value, options);
        },
        messageId: 'landing.pages.form.validations.title.empty',
        defaultMessage: 'Please enter a page name',
    },
];

export const Title = ({ className, skipValidations, disabled = false }) => {
    const {
        value: title,
        onChange: onChangeTitle,
        indicationData,
        scrollRef,
    } = useFormValidatedValue({
        field: 'title',
        validations,
        validationDependencies: { skipValidations },
    });
    const changeHandler = useCallback(
        event => {
            const rawValue = event.target.value.toLowerCase();
            const sanitizedValue = rawValue.replace(SLUG_SANITIZATION_REGEX, '');
            onChangeTitle(sanitizedValue);
        },
        [onChangeTitle]
    );
    const { formatMessage } = useIntl();
    const placeholder = formatMessage({
        id: 'landing.pages.form.title.placeholder',
        defaultMessage: 'e.g., summer-sale-2025',
    });

    return (
        <FormField
            ref={scrollRef}
            inputId="title"
            label={
                <FormattedMessage
                    id="landing.pages.form.title.input.label"
                    defaultMessage="Enter the advertorial URL (slug)"
                />
            }
            helpText={
                <TooltipSection>
                    <FormattedMessage
                        id="landing.pages.form.title.input.tooltip"
                        defaultMessage="This appears in your reports and in the article's URL."
                    />
                </TooltipSection>
            }
        >
            <InputWithIndication
                id="title"
                placeholder={placeholder}
                type={InputTypes.TEXT}
                value={title || ''}
                onChange={changeHandler}
                disabled={disabled}
                data-metrics-event-name={GTM_EVENTS.USABILITY}
                data-metrics-component="title"
                inputClass={className}
                inputWrapperClass={className}
                icon={<UrlPrefixIcon />}
                inputPadding={340}
                {...indicationData}
            />
        </FormField>
    );
};
